import React, { useState, useRef, Suspense, useCallback, useMemo } from "react";
import * as survey from "@aidkitorg/types/lib/survey";
import stableStringify from "fast-json-stable-stringify";
import { getDistroBrowserIncompatibility } from "../../Util";
import { useHistory, useLocation } from "react-router-dom";
import { usePost } from "../../API";
import { Button, useToast } from "@aidkitorg/component-library";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";
import { useAIPageRightPaneElement } from "../Admin/AIPage";
import { MessageTemplatePlaygroundChat, PlaygroundTracePanel } from "./MessageTemplatePlaygroundChat";
import { MessageTemplatePreview } from "./MessageTemplatePreview";
import type { CoachGenerationTrace } from "@aidkitorg/types/lib/convoai";

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

const blankTemplate: survey.EditableMessageTemplate = {
  kind: "Message Template",
  name: "",
  version: 0,
  description: "",
  instructions: []
};

type StoredTemplate = survey.MessageTemplate & {
  underlyingInstructions?: survey.Instruction[];
  underlyingOutput?: survey.OutputFormat;
};

type TemplateOption = {
  label: string;
  value: string;
  template: StoredTemplate;
};

function convertStoredTemplateToEditable(storedTemplate: StoredTemplate): survey.EditableMessageTemplate {
  const { underlyingInstructions, underlyingOutput, ...templateFields } = storedTemplate;
  const template: survey.EditableMessageTemplate = {
    ...templateFields,
    kind: 'Message Template',
  };

  if (underlyingInstructions) {
    template.instructions = underlyingInstructions;
  }

  if (underlyingOutput) {
    template.output = underlyingOutput;
  }

  return template;
}

export function MessageTemplateConfig() {
  const findTemplateRequest = usePost('/program/admin/prompts/list');
  const [loadedTemplate, setLoadedTemplate] = useState<survey.EditableMessageTemplate | null>(null);
  const location = useLocation();

  useAsyncEffect(async () => {
    const queryParams = new URLSearchParams(location.search)
    const name = queryParams.get('name');
    const version = queryParams.get('version');

    if (name) {
      const { items: templates } = await findTemplateRequest({ name, version: version ? parseInt(version) : undefined });
      const existingTemplate = templates?.[templates?.length - 1];
      if (existingTemplate) {
        const storedTemplate = { ...existingTemplate, kind: 'Message Template' } as StoredTemplate;
        setLoadedTemplate(convertStoredTemplateToEditable(storedTemplate));
        return;
      }
    }

    setLoadedTemplate(blankTemplate);
  }, []);

  if (!loadedTemplate) return <div>Loading...</div>;

  return <InnerMessageTemplateConfig initial={loadedTemplate} />;
}

export function InnerMessageTemplateConfig(props: { initial: survey.EditableMessageTemplate }) {
  const [messageTemplate, setMessageTemplate] = useState<survey.EditableMessageTemplate>(props.initial);
  const [latestVersion, setLatestVersion] = useState(props.initial.version);
  const [pristineSnapshot, setPristineSnapshot] = useState(props.initial);
  const [availableTemplates, setAvailableTemplates] = useState<TemplateOption[]>([]);
  const [latestTrace, setLatestTrace] = useState<CoachGenerationTrace | null>(null);
  const history = useHistory();
  const location = useLocation();

  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);

  const createTemplateRequest = usePost('/program/admin/prompts/create');
  const findTemplateRequest = usePost('/program/admin/prompts/list');
  const scanTemplatesRequest = usePost('/program/admin/prompts/scan');

  const { toast } = useToast();

  useAsyncEffect(async () => {
    const { nameToVersionsAndDescriptionsMap } = await scanTemplatesRequest({ latestOnly: true });

    const options: TemplateOption[] = Object.entries(nameToVersionsAndDescriptionsMap).map(([name, versionsAndDescriptions]) => ({
      label: `${name} (v${versionsAndDescriptions[0].version})`,
      value: `${name}:${versionsAndDescriptions[0].version}`,
      template: {
        kind: 'Message Template',
        name: name,
        version: versionsAndDescriptions[0].version,
        description: versionsAndDescriptions[0].description,
        instructions: [],
      } satisfies survey.MessageTemplate
    }));

    setAvailableTemplates(options);
  }, []);

  useAsyncEffect(async () => {
    const name = messageTemplate.name?.trim();
    if (!name) {
      setLatestVersion(0);
      return;
    }

    const templates = await findTemplateRequest({ name });
    const latest = (templates.items ?? [])
      .map(item => item.version)
      .filter(v => typeof v === 'number')
      .sort((a, b) => b - a)[0] ?? 0;

    setLatestVersion(latest);
  }, [messageTemplate.name]);

  const saveTemplate = useCallback(async () => {
    const oldVersion = messageTemplate.version ?? 0;
    const newVersion = (latestVersion ?? oldVersion) + 1;
    const newTemplate = { ...messageTemplate, version: newVersion };

    if (oldVersion > 0) {
      await createTemplateRequest({ item: newTemplate });
      toast({
        description: `created new template version: v${oldVersion} => v${newVersion}`,
        autoClose: true,
        duration: 1_500
      });
    } else {
      await createTemplateRequest({ item: newTemplate });
      toast({
        description: 'created new template!',
        autoClose: true,
        duration: 1_500
      });
    }
    setMessageTemplate(newTemplate);
    setLatestVersion(newTemplate.version);

    const queryParams = new URLSearchParams(location.search);
    const trimmedName = newTemplate.name?.trim();
    if (trimmedName) {
      queryParams.set('name', trimmedName);
    } else {
      queryParams.delete('name');
    }
    queryParams.set('version', `${newTemplate.version}`);

    history.replace({
      pathname: location.pathname,
      search: `?${queryParams.toString()}`,
      hash: location.hash,
    });

    setPristineSnapshot(newTemplate);
  }, [createTemplateRequest, history, location.hash, location.pathname, location.search, messageTemplate, latestVersion, toast]);

  const hasChanges = useMemo(() => {
    return stableStringify(messageTemplate) !== stableStringify(pristineSnapshot);
  }, [messageTemplate, pristineSnapshot]);

  const revertTemplate = useCallback(() => {
    distroRef.current?.initialize(pristineSnapshot);
  }, [pristineSnapshot]);


  const isDerivedTemplate = useMemo(() => {
    return messageTemplate.instructions.some(instruction =>
      instruction.kind === 'Message Template Reference'
    ) || (messageTemplate.output?.type === 'template');
  }, [messageTemplate.instructions, messageTemplate.output]);

  const traceTabName = useMemo(() => {
    if (!latestTrace) {
      return 'Trace';
    }

    const hasFailure = latestTrace.delegation.some(entry => entry.status === 'failed');
    return hasFailure ? 'Trace: failed' : 'Trace: ok';
  }, [latestTrace]);

  useAIPageRightPaneElement(() => ({
    key: "message-template-playground-chat",
    name: "Playground chat",
    order: 100,
    show: true,
    children: (
      <div className="p-2 h-full min-h-0">
        <MessageTemplatePlaygroundChat
          template={messageTemplate}
          unsavedEdits={hasChanges}
          onTrace={setLatestTrace}
        />
      </div>
    )
  }), [hasChanges, messageTemplate, pristineSnapshot]);

  useAIPageRightPaneElement(() => ({
    key: "message-template-trace",
    name: traceTabName,
    order: 110,
    show: true,
    children: (
      <div className="p-4 h-full overflow-auto">
        <PlaygroundTracePanel trace={latestTrace} />
      </div>
    )
  }), [latestTrace, traceTabName]);

  useAIPageRightPaneElement(() => ({
    key: "message-template-preview",
    name: "Preview",
    order: 90,
    show: true,
    children: (
      <div className="p-4 h-full overflow-auto">
        <MessageTemplatePreview template={messageTemplate} />
      </div>
    )
  }), [messageTemplate]);

  const incompatible = getDistroBrowserIncompatibility();
  if (incompatible) return incompatible;

  return (
    <div className="bg-gray-100 h-full p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Message Template Management</h1>
      </div>

      <div className="grid gap-4 grid-cols-1">
        <div className="bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg space-y-4 p-4">
          <h2 className="text-lg font-semibold">Template Editor</h2>

          <Suspense fallback={<div>Loading editor...</div>}>
            <Distro
              ref={distroRef}
              value={messageTemplate}
              types='src/survey.ts'
              llmContext={`We are using a tool to configure message templates for AI conversations.
              Message templates define the structure and instructions for AI interactions.
              
              You can reference other message templates in two ways:
              
              1. **As Instructions**: Add a "Message Template Reference" instruction anywhere in your instructions array
                 - Select the template name and version to reference
                 - Optionally set a role (user/system/assistant) to override the referenced instructions' roles
                 - The referenced template's instructions will be inserted at that exact position
              
              2. **As Output Format**: Set the output format type to "template" 
                 - Reference another template's output format
                 - The referenced template's output schema will be used
              
              All template references are resolved when you save the template.
              This makes templates modular and reusable!
              
              Available templates: ${availableTemplates.map(t => t.template.name).join(', ')}`}
              name='EditableMessageTemplate'
              onChange={(v) => {
                setMessageTemplate(v)
              }}
            />
          </Suspense>

          <div className='flex flex-col items-center gap-8'>
            <div className='flex flex-row gap-4'>
              {hasChanges && (
                <Button
                  className="ml-2 mb-2"
                  variant="secondary"
                  onClick={revertTemplate}>
                  Revert
                </Button>
              )}
              <Button
                className="ml-2 mb-2"
                variant="primary"
                onClick={saveTemplate}>
                {isDerivedTemplate
                  ? `Save Derived Template (v${(latestVersion ?? messageTemplate.version) + 1})`
                  : `Save New Version (v${(latestVersion ?? messageTemplate.version) + 1})`
                }
              </Button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
